import { useState } from 'react';
import { createFileRoute, Link, Outlet, useMatchRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import { useHasPermission } from '@/lib/permissions';
import { INVOICE_STATUS_LABELS, dayFr, gnf, invoiceBadge } from '@/lib/invoices';
import { requirePermission } from '@/lib/route-guards';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import type { Client, CustomerInvoice, CustomerInvoiceDetail } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/invoices')({
  beforeLoad: () => requirePermission('invoices.read'),
  component: InvoicesPage,
});

const ALL = 'all';
const OVERDUE = 'overdue';

function InvoicesPage() {
  const matchRoute = useMatchRoute();
  if (matchRoute({ to: '/invoices/$id' })) return <Outlet />;
  return <InvoicesList />;
}

function InvoicesList() {
  const [status, setStatus] = useState(ALL);
  const [clientId, setClientId] = useState(ALL);
  const [creating, setCreating] = useState(false);
  const canWrite = useHasPermission('invoices.write');

  const { data: clients } = useQuery({
    queryKey: ['clients', { showInactive: false }],
    queryFn: async () => (await api.get<Client[]>('/clients')).data,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['customer-invoices', { status, clientId }],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '200' });
      if (status === OVERDUE) params.set('overdue', 'true');
      else if (status !== ALL) params.set('status', status);
      if (clientId !== ALL) params.set('clientId', clientId);
      return (await api.get<{ data: CustomerInvoice[]; total: number }>(`/customer-invoices?${params}`)).data;
    },
  });

  // Synthèse : reste à encaisser sur les factures émises (hors brouillons / annulées)
  const open = data?.data.filter((i) => i.status === 'ISSUED' || i.status === 'PARTIALLY_PAID') ?? [];
  const toCollect = open.reduce((s, i) => s + i.total - i.amountPaid, 0);
  const overdue = open.filter((i) => i.overdue).reduce((s, i) => s + i.total - i.amountPaid, 0);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Facturation clients</h1>
        {canWrite && <Button onClick={() => setCreating(true)}><Plus className="h-4 w-4 mr-2" /> Nouvelle facture</Button>}
      </div>

      <div className="grid grid-cols-2 gap-4 max-w-xl">
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground">Reste à encaisser</p>
          <p className="text-xl font-bold">{gnf(toCollect)}</p>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground">Dont en retard</p>
          <p className={`text-xl font-bold ${overdue > 0 ? 'text-danger' : ''}`}>{gnf(overdue)}</p>
        </div>
      </div>

      <div className="flex gap-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tous les statuts</SelectItem>
            <SelectItem value={OVERDUE}>En retard</SelectItem>
            {Object.entries(INVOICE_STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={clientId} onValueChange={setClientId}>
          <SelectTrigger className="w-60"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tous les clients</SelectItem>
            {clients?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>N°</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Période</TableHead>
              <TableHead>Échéance</TableHead>
              <TableHead className="text-right">Total TTC</TableHead>
              <TableHead className="text-right">Reste dû</TableHead>
              <TableHead>Statut</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 4 }).map((_, i) => (
              <TableRow key={i}>{Array.from({ length: 7 }).map((__, j) => <TableCell key={j}><Skeleton className="h-4" /></TableCell>)}</TableRow>
            ))}
            {data?.data.map((i) => (
              <TableRow key={i.id}>
                <TableCell>
                  <Link to="/invoices/$id" params={{ id: i.id }} className="font-mono font-semibold text-primary-500 hover:underline">
                    {i.number ?? 'Brouillon'}
                  </Link>
                </TableCell>
                <TableCell>{i.client.name}</TableCell>
                <TableCell>{dayFr(i.periodStart)} – {dayFr(i.periodEnd)}</TableCell>
                <TableCell>{dayFr(i.dueDate)}</TableCell>
                <TableCell className="text-right">{gnf(i.total, i.currency)}</TableCell>
                <TableCell className="text-right">
                  {i.status === 'CANCELLED' || i.status === 'DRAFT' ? '—' : gnf(i.total - i.amountPaid, i.currency)}
                </TableCell>
                <TableCell><Badge variant={invoiceBadge(i).variant}>{invoiceBadge(i).label}</Badge></TableCell>
              </TableRow>
            ))}
            {data && data.data.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Aucune facture</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <NewInvoiceDialog open={creating} onOpenChange={setCreating} clients={clients ?? []} />
    </div>
  );
}

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Facture mensuelle : par défaut le mois précédent. */
function NewInvoiceDialog({ open, onOpenChange, clients }: { open: boolean; onOpenChange: (o: boolean) => void; clients: Client[] }) {
  const nav = useNavigate();
  const now = new Date();
  const [clientId, setClientId] = useState('');
  const [from, setFrom] = useState(ymd(new Date(now.getFullYear(), now.getMonth() - 1, 1)));
  const [to, setTo] = useState(ymd(new Date(now.getFullYear(), now.getMonth(), 0)));

  const create = useMutation({
    mutationFn: () =>
      api.post<CustomerInvoiceDetail>('/customer-invoices', { clientId, periodStart: from, periodEnd: to }),
    onSuccess: (res) => {
      onOpenChange(false);
      toast.success(`Brouillon créé : ${res.data.lines.length} mission(s)`);
      void nav({ to: '/invoices/$id', params: { id: res.data.id } });
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Création impossible')),
  });

  const client = clients.find((c) => c.id === clientId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Nouvelle facture</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Client</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger><SelectValue placeholder="Choisir un client" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {client && !client.billingMode && (
              <p className="text-xs text-danger mt-1">
                Ce client n&apos;a pas de tarif : renseignez-le dans sa fiche (onglet « Tarif &amp; factures »).
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="inv-from">Du</Label>
              <Input id="inv-from" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="inv-to">Au</Label>
              <Input id="inv-to" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Toutes les missions terminées du client sur la période et pas encore facturées sont reprises dans un
            brouillon, que vous pourrez ajuster avant de l&apos;émettre.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={() => create.mutate()} disabled={!clientId || !from || !to || create.isPending}>
            {create.isPending ? 'Préparation…' : 'Préparer la facture'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
